import React, { useEffect, useState, useMemo } from 'react';

// Components
import {
  ScrollView,
  Text,
  HStack,
  VStack,
  Box,
  Skeleton,
  Heading,
} from 'native-base';
import {
  ChatBubble,
  Vibe,
  BasicsTag,
  SecondaryButton,
  PrimaryButton,
  FeedbackRating,
  UserList,
  ChatStreak,
} from '../../';

// Assets
import { theme } from '@breakroom-nx/assets';
import { LogoutIcon, ClockIcon, FireIcon } from 'react-native-heroicons/solid';

// Classes & Types
import { Room, Streak } from '@breakroom-nx/classes';
import {
  useGetMembersForRoomQuery,
  useAppSelector,
  getCurrentUser,
  useUserLeftChatMutation,
  useAppDispatch,
  openModal,
  useGetCurrentUserQuery,
  toggleChatRoomPanel,
} from '@breakroom-nx/redux';
import {
  GeoData,
  Status,
  EntityBeingReported,
  timeFrom,
} from '@breakroom-nx/helpers';
import { reverseGeo } from '@breakroom-nx/apis';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import {
  ArrowLeftOnRectangleIcon,
  UserPlusIcon,
} from '@heroicons/react/24/solid';

export interface ChatInfoProps {
  room: Room;
}

export function ChatInfo({ room }: ChatInfoProps) {
  const dispatch = useAppDispatch();
  const { data: currentUser } = useGetCurrentUserQuery();
  const streak = useMemo(() => new Streak(room.streakDuration()), [room]);
  const [geoData, setGeoData] = useState<GeoData | undefined>(undefined);

  const {
    data: members,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  } = useGetMembersForRoomQuery(room, {
    skip: !room, // Skip API call if room is not defined
  });

  const [leaveChat, chatLeaveState] = useUserLeftChatMutation();

  useEffect(() => {
    if (room && room.location !== null) {
      reverseGeo(room.location!).then((geoData: GeoData | undefined) => {
        if (geoData) {
          setGeoData(geoData);
        }
      });
    }
  }, [room]);

  useEffect(() => {
    // console.log(`Rendering Chat Info`);
  });

  return (
    <ScrollView
      flex={1}
      showsVerticalScrollIndicator={true}
      _contentContainerStyle={{
        pb: 100,
      }}
    >
      <VStack space={5} pt={10}>
        {!room ? (
          <VStack space={8} alignItems={'center'} width={'full'} pb={10}>
            <Skeleton
              h="8"
              w={'1/2'}
              rounded="full"
              startColor={theme.colors.yellow[100]}
              endColor={theme.colors.yellow[200]}
            />
            <Skeleton
              width={'90px'}
              height={'90px'}
              rounded="full"
              startColor={theme.colors.surface[200]}
              endColor={theme.colors.surface[400]}
            />
            <VStack space={3} alignItems={'center'} width={'full'}>
              <Skeleton
                h="5"
                w={'1/3'}
                rounded="full"
                startColor={theme.colors.surface[200]}
                endColor={theme.colors.surface[400]}
              />
            </VStack>
            <Skeleton.Text
              lines={3}
              alignItems="center"
              px="12"
              startColor={theme.colors.surface[200]}
              endColor={theme.colors.surface[400]}
            />
          </VStack>
        ) : (
          <VStack alignItems={'center'} space={8}>
            <VStack alignItems={'center'} space={7}>
              <Heading fontSize={35} lineHeight={38}>
                {room.title}
              </Heading>
              <ChatBubble room={room} disabled={true} size={90} />

              <VStack space={3}>
                <ChatStreak
                  created={room.created}
                  expires={room.expires}
                  streak={streak}
                  verbose
                />

                {/* {geoData ? (
                <BasicsTag
                  icon={<span className={`fi fi-${geoData.code}`}></span>}
                  text={`${geoData.city}, ${geoData.code.toUpperCase()}`}
                />
              ) : null} */}
              </VStack>
            </VStack>
            <VStack alignItems={'center'} space={5}>
              <Vibe
                text={room.vibe}
                icon={room.vibeIcon}
                key={room.vibe}
                focused
              />
            </VStack>

            <FeedbackRating
              uid={room.id}
              name={room.title}
              entity={EntityBeingReported.Room}
            />

            {currentUser &&
              members &&
              Array.isArray(members) &&
              members.some((m) => m.uid === currentUser.uid) && (
                <HStack m={5} flexWrap={'wrap'} justifyContent={'center'}>
                  <Box py={2.5} px={2.5}>
                    <PrimaryButton
                      text="Invite to Room"
                      onPress={() =>
                        dispatch(openModal({ modalType: 'UserInviteList' }))
                      }
                      startIcon={
                        <UserPlusIcon
                          color="black"
                          width={'16px'}
                          height={'16px'}
                        />
                      }
                    />
                  </Box>
                  <Box py={2.5} px={2.5}>
                    <SecondaryButton
                      text={'Leave Room'}
                      isLoading={chatLeaveState.status === QueryStatus.pending}
                      startIcon={
                        <ArrowLeftOnRectangleIcon
                          color={theme.colors.red[200]}
                          width={'16px'}
                          height={'16px'}
                        />
                      }
                      disabled={
                        currentUser == undefined ||
                        chatLeaveState.status === QueryStatus.pending
                      }
                      onPress={async () => {
                        // console.log(`Leave Room`);
                        if (currentUser) {
                          await leaveChat({
                            user: currentUser,
                            roomId: room.id,
                          })
                            .then(() => {
                              dispatch(toggleChatRoomPanel());
                            })
                            .catch((err) => {
                              dispatch(
                                openModal({
                                  modalType: 'Alert',
                                  modalProps: {
                                    status: Status.error,
                                    title:
                                      'There was an error leaving the room',
                                    description: 'Please try again.',
                                  },
                                })
                              );
                            });
                        }
                      }}
                      color={theme.colors.red[200]}
                      fontSize={14}
                      bg={'transparent'}
                      style={{
                        borderWidth: 1,
                        borderColor: theme.colors.red[200],
                      }}
                    />
                  </Box>
                </HStack>
              )}
          </VStack>
        )}
        <UserList
          title={'Members'}
          data={members}
          isLoading={isLoading}
          isSuccess={isSuccess}
          isError={isError}
          error={error}
          refetch={refetch}
        />
      </VStack>
    </ScrollView>
  );
}

// Wrap ChatInfo with React.memo to prevent unnecessary re-renders
export default React.memo(ChatInfo, (prevProps, nextProps) => {
  return prevProps.room.id === nextProps.room.id;
});
